import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import PageHeader from '../components/ui/PageHeader';
import { useToast } from '../components/ui/Toast';
import { getMyAvailability, setMyAvailability } from '../api/availability';

const WEEKDAYS = [
  { dow: 1, label: 'Monday' },
  { dow: 2, label: 'Tuesday' },
  { dow: 3, label: 'Wednesday' },
  { dow: 4, label: 'Thursday' },
  { dow: 5, label: 'Friday' },
  { dow: 6, label: 'Saturday' },
  { dow: 0, label: 'Sunday' },
];

const DEFAULT_START = '09:00';
const DEFAULT_END = '17:00';

function timeToMinutes(hhmm) {
  if (!hhmm) return 0;
  const [h, m] = hhmm.split(':').map((x) => parseInt(x, 10));
  return (h || 0) * 60 + (m || 0);
}

function minutesToTime(mins) {
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

function defaultWeekState() {
  return WEEKDAYS.reduce((acc, { dow }) => {
    acc[dow] = {
      active: dow >= 1 && dow <= 5,
      startTime: DEFAULT_START,
      endTime: DEFAULT_END,
    };
    return acc;
  }, {});
}

export default function HostAvailabilityPage() {
  const toast = useToast();
  const [week, setWeek] = useState(defaultWeekState);
  const [timezone] = useState(
    Intl.DateTimeFormat().resolvedOptions().timeZone || 'Asia/Kolkata'
  );
  const [slotDurationMinutes] = useState(30);
  const [bufferMinutes] = useState(10);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getMyAvailability();
      const rows = Array.isArray(res.data) ? res.data : [];
      const next = defaultWeekState();
      rows.forEach((row) => {
        const dow = row.dayOfWeek;
        if (next[dow] == null) return;
        next[dow] = {
          active: true,
          startTime: minutesToTime(row.startMinuteOfDay),
          endTime: minutesToTime(row.endMinuteOfDay),
        };
      });
      setWeek(next);
    } catch (err) {
      console.warn('[HostAvailability] load failed:', err.message);
      toast.error('Could not load your availability.');
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    load();
  }, [load]);

  const activeDayCount = useMemo(
    () => Object.values(week).filter((d) => d.active).length,
    [week]
  );

  const handleToggle = (dow, active) => {
    setWeek((prev) => ({
      ...prev,
      [dow]: { ...prev[dow], active },
    }));
  };

  const handleTime = (dow, field, value) => {
    setWeek((prev) => ({
      ...prev,
      [dow]: { ...prev[dow], [field]: value },
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const days = WEEKDAYS.filter(({ dow }) => week[dow]?.active).map(({ dow }) => {
        const row = week[dow];
        const startMinuteOfDay = timeToMinutes(row.startTime);
        const endMinuteOfDay = timeToMinutes(row.endTime);
        if (endMinuteOfDay <= startMinuteOfDay) {
          throw new Error(`${WEEKDAYS.find((w) => w.dow === dow)?.label}: end must be after start`);
        }
        return {
          dayOfWeek: dow,
          startMinuteOfDay,
          endMinuteOfDay,
          timezone,
          slotDurationMinutes,
          bufferMinutes,
        };
      });

      await setMyAvailability(days);
      toast.success('Availability saved.');
      await load();
    } catch (err) {
      const msg = err.message || err?.response?.data?.message || 'Save failed.';
      toast.error(typeof msg === 'string' ? msg : 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-8">
      <PageHeader
        title="My availability"
        subtitle="Set your weekly interview hours. Scheduling picks open slots and blocks double-bookings."
      />

      <p className="text-sm text-slate-500 mb-6">
        v1 supports one recurring weekly pattern only. Per-date overrides, holidays, and vacation
        blackouts are not supported yet.
      </p>

      {loading ? (
        <p className="text-sm text-slate-500">Loading…</p>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="divide-y divide-slate-100">
            {WEEKDAYS.map(({ dow, label }) => {
              const row = week[dow];
              return (
                <div key={dow} className="flex flex-wrap items-center gap-4 px-5 py-4">
                  <label className="flex items-center gap-2 min-w-[8rem] cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={!!row.active}
                      onChange={(e) => handleToggle(dow, e.target.checked)}
                      className="w-4 h-4 accent-teal-600"
                    />
                    <span className="text-sm font-medium text-slate-800">{label}</span>
                  </label>
                  {row.active ? (
                    <div className="flex items-center gap-2 flex-wrap">
                      <input
                        type="time"
                        value={row.startTime}
                        onChange={(e) => handleTime(dow, 'startTime', e.target.value)}
                        className="input text-sm"
                      />
                      <span className="text-slate-400 text-sm">to</span>
                      <input
                        type="time"
                        value={row.endTime}
                        onChange={(e) => handleTime(dow, 'endTime', e.target.value)}
                        className="input text-sm"
                      />
                    </div>
                  ) : (
                    <span className="text-xs text-slate-400">Not working this day</span>
                  )}
                </div>
              );
            })}
          </div>
          <div className="px-5 py-4 bg-slate-50 border-t border-slate-100 flex items-center justify-between gap-4 flex-wrap">
            <p className="text-xs text-slate-500">
              {activeDayCount === 0
                ? 'No days enabled — scheduling falls back to manual date/time entry.'
                : `${activeDayCount} active day(s) · ${slotDurationMinutes} min slots · ${bufferMinutes} min buffer · ${timezone}`}
            </p>
            <div className="flex gap-2">
              <Link to="/settings" className="btn btn-secondary text-sm px-4 py-2">
                Back
              </Link>
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="btn btn-primary text-sm px-4 py-2 disabled:opacity-50"
              >
                {saving ? 'Saving…' : 'Save availability'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
